/**
 * Centralized background rendering service
 * Handles all background image updates to eliminate duplication across the codebase
 */
import { getBackgroundService } from './BackgroundService.js';

export class BackgroundRenderer {
  static instance = null;

  static getInstance() {
    if (!BackgroundRenderer.instance) {
      BackgroundRenderer.instance = new BackgroundRenderer();
    }
    return BackgroundRenderer.instance;
  }

  constructor() {
    this.canvasElementId = 'background-canvas';
  }

  /**
   * Applies a background image to the canvas with direct CSS application
   * @param {string} url - The background image URL to apply
   * @param {Object} [options={}] - Additional options
   * @param {boolean} [options.skipPlaceholderCheck=false] - Skip checking for placeholder locations (0, 1)
   * @param {Function} [options.onLoad=null] - Callback when image loads successfully
   * @param {Function} [options.onError=null] - Callback when image fails to load
   * @returns {Promise<void>}
   */
  async applyBackgroundImage(url, options = {}) {
    const {
      skipPlaceholderCheck = false,
      onLoad = null,
      onError = null
    } = options;

    // Skip loading for placeholder locations unless explicitly told not to check
    if (!skipPlaceholderCheck && url && (url.includes('/0.') || url.includes('/1.'))) {
      console.log('BackgroundRenderer: Skipping background load for placeholder location:', url);
      return;
    }

    const backgroundCanvas = document.getElementById(this.canvasElementId);
    if (!backgroundCanvas) {
      console.warn('BackgroundRenderer: Background canvas not found');
      return;
    }

    let resolvedUrl = url;
    try {
      resolvedUrl = await getBackgroundService().resolveExistingBackgroundFromUrl(url);
    } catch (error) {
      console.warn('BackgroundRenderer: background image failed all variations:', url, error);
      if (onError) onError();
      throw error;
    }

    backgroundCanvas.style.backgroundImage = `url(${resolvedUrl})`;
    backgroundCanvas.style.backgroundSize = 'cover';
    backgroundCanvas.style.backgroundPosition = 'center';
    backgroundCanvas.style.backgroundRepeat = 'no-repeat';

    if (onLoad) onLoad();
  }

  /**
   * Updates background based on background data (string URL or object with locationId/timeOfDay)
   * @param {string|Object} backgroundData - Background data to resolve to URL
   * @param {Object} [options={}] - Options passed to applyBackgroundImage
   * @returns {Promise<void>}
   */
  async updateBackground(backgroundData, options = {}) {
    const url = await this.resolveBackgroundUrl(backgroundData);
    if (url) {
      await this.applyBackgroundImage(url, options);
    }
  }

  /**
   * Resolves background data to a URL string
   * @param {string|Object} backgroundData - Background data
   * @returns {Promise<string|null>} The resolved URL or null
   */
  async resolveBackgroundUrl(backgroundData) {
    if (typeof backgroundData === 'string') {
      return backgroundData;
    }

    const bgService = getBackgroundService();

    if (backgroundData?.locationId && backgroundData?.timeOfDay) {
      return bgService.constructBackgroundUrl(backgroundData.locationId, backgroundData.timeOfDay);
    }

    const current = await bgService.getCurrentBackground();
    if (typeof current === 'string') return current;
    if (current?.locationId && current?.timeOfDay) {
      return bgService.constructBackgroundUrl(current.locationId, current.timeOfDay);
    }

    return null;
  }

  /**
   * Sets the canvas element ID to use for background updates
   * @param {string} elementId - The element ID to use
   */
  setCanvasElementId(elementId) {
    this.canvasElementId = elementId;
  }
}

export const backgroundRenderer = BackgroundRenderer.getInstance();
